import { useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, X } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  addEventRole,
  eventRolesQueryOptions,
  orgMembersQueryOptions,
  removeEventRole,
  type EventRole,
  type EventRoleRow,
  type OrgMember,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import { Avatar } from "@/components/ui/Avatar"
import {
  Card,
  GhostButton,
  Pill,
  Select,
  SettingsPage,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/permissions")({
  component: PermissionsView,
})

const ROLE_SECTIONS: { role: EventRole; title: string; blurb: string }[] = [
  {
    role: "organizer",
    title: "Organizers",
    blurb:
      "Full write access to this event. Plain org members must be an organizer here to manage it.",
  },
  {
    role: "contractor",
    title: "Contractors",
    blurb: "Read-only event access — schedule and channels, no edits.",
  },
]

function PermissionsView() {
  const { eventId, organizationId } = useActiveEvent()
  const qc = useQueryClient()

  const rolesQ = useQuery({ ...eventRolesQueryOptions(eventId ?? ""), enabled: !!eventId })
  const membersQ = useQuery({
    ...orgMembersQueryOptions(organizationId ?? ""),
    enabled: !!organizationId,
  })

  const roles = rolesQ.data ?? []
  const members = membersQ.data ?? []

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["resource", "event_roles", { eventId }] })

  return (
    <SettingsPage
      title="Permissions"
      description="Grant per-event roles to members of this organization."
    >
      {(rolesQ.isError || membersQ.isError) && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {((rolesQ.error ?? membersQ.error) as Error)?.message ?? "Failed to load permissions"}
          </p>
        </Card>
      )}

      {ROLE_SECTIONS.map((s) => (
        <RoleSection
          key={s.role}
          eventId={eventId}
          organizationId={organizationId}
          role={s.role}
          title={s.title}
          blurb={s.blurb}
          roles={roles}
          members={members}
          onChanged={invalidate}
        />
      ))}
    </SettingsPage>
  )
}

function RoleSection({
  eventId,
  organizationId,
  role,
  title,
  blurb,
  roles,
  members,
  onChanged,
}: {
  eventId: string | undefined
  organizationId: string | undefined
  role: EventRole
  title: string
  blurb: string
  roles: EventRoleRow[]
  members: OrgMember[]
  onChanged: () => void
}) {
  const [pick, setPick] = useState("")

  const assigned = useMemo(
    () => roles.filter((r) => r.role === role),
    [roles, role],
  )
  const assignedUserIds = new Set(assigned.map((r) => r.linkedUserId))
  const available = members.filter((m) => !assignedUserIds.has(m.userId))

  const memberById = useMemo(() => {
    const map = new Map<string, OrgMember>()
    for (const m of members) map.set(m.userId, m)
    return map
  }, [members])

  const add = useMutation({
    mutationFn: () => {
      if (!eventId || !organizationId || !pick) throw new ApiError("Pick a member", 400)
      return addEventRole({ eventId, organizationId, linkedUserId: pick, role })
    },
    onSuccess: () => {
      setPick("")
      toast.success(`Added ${title.slice(0, -1).toLowerCase()}`)
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to add role"),
  })

  const remove = useMutation({
    mutationFn: (roleId: string) => removeEventRole(roleId),
    onSuccess: () => {
      toast.success("Removed")
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to remove role"),
  })

  return (
    <Card title={title} description={blurb}>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          className="flex-1"
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          disabled={available.length === 0}
        >
          <option value="">
            {available.length === 0 ? "No more members to add" : "Select a member…"}
          </option>
          {available.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.user.name ?? m.user.email}
            </option>
          ))}
        </Select>
        <GhostButton onClick={() => add.mutate()} disabled={!pick || add.isPending}>
          <Plus className="h-4 w-4" />
          Add
        </GhostButton>
      </div>

      {assigned.length === 0 ? (
        <p className="text-[12.5px] text-plum-700/45 dark:text-plum-200/35">
          No {title.toLowerCase()} yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {assigned.map((r) => {
            const m = memberById.get(r.linkedUserId)
            return (
              <li
                key={r.id}
                className="flex items-center gap-2.5 rounded-xl border border-plum-200/60 px-3 py-2 dark:border-white/10"
              >
                <Avatar
                  name={m?.user.name ?? m?.user.email ?? "?"}
                  kind="organizer"
                  userId={r.linkedUserId}
                  size={28}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                    {m?.user.name ?? m?.user.email ?? r.linkedUserId}
                  </p>
                  {m?.user.name && (
                    <p className="truncate text-[11.5px] text-plum-700/50 dark:text-plum-200/35">
                      {m.user.email}
                    </p>
                  )}
                </div>
                {m && <Pill>{m.role}</Pill>}
                <button
                  type="button"
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-plum-700/50 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:text-plum-200/40 dark:hover:bg-rose-500/10"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

